import { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';
import Navbar from '../../Components/Navbar';

const ResetPassword = () => {
  const { uidb64, token } = useParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation('ResetPassword');

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setMessage('');
  setIsSubmitting(true);

  try {
    if (newPassword !== confirmPassword) {
      throw new Error(t('resetPassword.validation.passwordMatch'));
    }
    if (newPassword.length < 8) {
      throw new Error(t('resetPassword.validation.passwordLength'));
    }

    const response = await api.post(`/reset-password/${uidb64}/${token}/`, {
      new_password: newPassword 
    });

    setMessage(response.data.message || t('resetPassword.messages.success'));
    setTimeout(() => navigate('/login'), 3000);

  } catch (err) {
    console.error('Reset password error:', err);
    
    if (err.response?.data?.error) {
      setError(err.response.data.error);
    } else if (err.response?.status === 400) {
      setError(t('resetPassword.messages.error.resetFailed'));
    } else {
      setError(err.message || t('resetPassword.messages.error.generic'));
    }
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-black)]">

      <div className="absolute top-0 left-0 w-full">
        <Navbar minimal />
      </div>

      <div className="bg-[var(--bg-black)] p-8 border border-solid border-white/40 rounded-2xl shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold text-[var(--text-black)] mb-6 bg-transparent">{t('resetPassword.title')}</h2>
        
        {message && (
          <div className="mb-4 p-4 bg-green-900 text-green-100 rounded-lg">
            {message}
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-4 bg-red-900 text-red-100 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 bg-transparent">
          <div className='bg-transparent'>
            <label htmlFor="newPassword" className="block text-[var(--text-black)] mb-2 bg-transparent">
              {t('resetPassword.form.newPassword.label')}
            </label>
            <input
              id="newPassword"
              type="password"
              placeholder={t('resetPassword.form.newPassword.placeholder')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength="8"
              className="w-full px-4 py-2 bg-black text-[var(--text-black)] rounded-lg border border-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className='bg-transparent'>
            <label htmlFor="confirmPassword" className="block text-[var(--text-black)] mb-2 bg-transparent">
              {t('resetPassword.form.confirmPassword.label')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              placeholder={t('resetPassword.form.confirmPassword.placeholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength="8"
              className="w-full px-4 py-2 bg-black text-[var(--text-black)] rounded-lg border border-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <motion.button
            animate={{
              background: [
                "linear-gradient(90deg, #FF1493, #FF69B4)", 
                "linear-gradient(90deg, #FF6347, #FF1493)",
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="w-full text-[var(--text-black)] py-2 rounded-lg text-lg font-semibold border-none flex justify-center items-center"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center bg-transparent">
                <svg className="bg-transparent animate-spin -ml-1 mr-2 h-5 w-5 text-[var(--text-black)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className='"opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" bg-transparent'></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                t('resetPassword.form.submitting')
              </span>
            ) : (
              t('resetPassword.form.submit')
            )}
          </motion.button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;